import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { CampaignSettings } from '@/api/dashboard'
import { CenteredProgress } from '@/components'
import { useGameSettingsCreateMutate, useGameSettingsQuery, useGameSettingsUpdateMutate } from '../../api'
import { LIVEOPS_PATH_TABLE, useGenerateCompanyPath } from '@/libs'
import { FullscreenLayout } from '@/components/layouts'
import { BackButton, PageHeader, SuperAdminInfo, ToastSeverity, useToast } from '@/ui'
import { useBanner } from '@/libs/hooks/useBanner'
import { FrequencyGroup } from './FrequencyGroup'
import { FrequencyCard } from './FrequencyCard'
import { Button, Toggle, cn } from '@dashboard/ui'
import { useCurrentUser } from '@/api/useCurrentUser'
import { Settings } from '../../icons/Settings'

export const CampaignSettingsPage = () => {
  const { t } = useTranslation()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { isSuperAdmin } = useCurrentUser()
  const { getStickyHeight } = useBanner()
  const { data: settings, isLoading } = useGameSettingsQuery(companyId, gameId)
  const { mutateAsync: createMutateAsync } = useGameSettingsCreateMutate(companyId, gameId)
  const { mutateAsync: updateMutateAsync } = useGameSettingsUpdateMutate(companyId, gameId)
  const showToast = useToast()
  const navigate = useNavigate()

  const formMethods = useForm<CampaignSettings>({
    values: {
      ...settings?.campaign_settings,
    },
  })
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isDirty },
  } = formMethods

  const [liveops_advanced_mode_enabled, node_analytics_enabled] = watch([
    'liveops_advanced_mode_enabled',
    'node_analytics_enabled',
  ])

  const onSubmit = handleSubmit(async data => {
    if (!data.email_frequency?.period_type) {
      data.email_frequency = null as unknown as undefined
    }

    if (!data.mobile_popup_frequency?.period_type) {
      data.mobile_popup_frequency = null as unknown as undefined
    }

    if (!data.mobile_push_frequency?.period_type) {
      data.mobile_push_frequency = null as unknown as undefined
    }

    if (!data.hub_popup_frequency?.period_type) {
      data.hub_popup_frequency = null as unknown as undefined
    }

    if (!data.hub_push_frequency?.period_type) {
      data.hub_push_frequency = null as unknown as undefined
    }

    if (!data.all_frequency?.period_type) {
      data.all_frequency = null as unknown as undefined
    }

    if (!settings) {
      await createMutateAsync({
        data: { campaign_settings: { ...data } as CampaignSettings },
      })
    } else {
      await updateMutateAsync({
        settingsId: settings.id,
        data: { ...settings, campaign_settings: { ...data } as CampaignSettings },
      })
    }

    reset(data)
    showToast({ message: t('saved'), severity: ToastSeverity.success })
  })

  const renderHeader = () => {
    return (
      <>
        <PageHeader
          className="px-4"
          extra={
            <Button variant={'default'} disabled={!isDirty} onClick={onSubmit}>
              {t('campaign_settings.frequency.form.save')}
            </Button>
          }
        >
          <BackButton
            onClick={() => navigate(generatePath(LIVEOPS_PATH_TABLE, { companyId, gameId }))}
            text={t('campaign_settings.frequency.title')}
            icon={<Settings size={34} />}
          />
        </PageHeader>
      </>
    )
  }

  if (isLoading) {
    return (
      <FullscreenLayout>
        <div className={cn('flex flex-col bg-bg-primary', getStickyHeight())}>
          {renderHeader()}
          <div className="flex size-full items-center justify-center">
            <CenteredProgress />
          </div>
        </div>
      </FullscreenLayout>
    )
  }

  return (
    <FullscreenLayout>
      <div className="flex flex-col overflow-auto bg-bg-primary">
        {renderHeader()}
        <section className="mx-auto px-2 py-0" style={{ maxWidth: '1200px' }}>
          <div style={{ padding: '20px 0' }}>
            <FormProvider {...formMethods}>
              <FrequencyGroup label={t('campaign_settings.frequency.groups.all')}>
                <FrequencyCard
                  label={t('campaign_settings.frequency.all.label')}
                  desc={t('campaign_settings.frequency.all.desc')}
                  field="all_frequency"
                />
              </FrequencyGroup>

              <FrequencyGroup label={t('campaign_settings.frequency.groups.game_hub')}>
                <FrequencyCard
                  label={t('campaign_settings.frequency.hub_popup.label')}
                  desc={t('campaign_settings.frequency.hub_popup.desc')}
                  field="hub_popup_frequency"
                />
              </FrequencyGroup>

              <FrequencyGroup label={t('campaign_settings.frequency.groups.email')}>
                <FrequencyCard
                  label={t('campaign_settings.frequency.email.label')}
                  desc={t('campaign_settings.frequency.email.desc')}
                  field="email_frequency"
                />
              </FrequencyGroup>

              <FrequencyGroup label={t('campaign_settings.frequency.groups.app')}>
                <FrequencyCard
                  label={t('campaign_settings.frequency.mobile_popup.label')}
                  desc={t('campaign_settings.frequency.mobile_popup.desc')}
                  field="mobile_popup_frequency"
                />

                <FrequencyCard
                  label={t('campaign_settings.frequency.mobile_push.label')}
                  desc={t('campaign_settings.frequency.mobile_push.desc')}
                  field="mobile_push_frequency"
                />
              </FrequencyGroup>
            </FormProvider>
          </div>

          <div className="mb-6">
            <FrequencyGroup label={t('campaign_settings.advanced-settings')}>
              <div className="rounded-xl border border-border-primary bg-fg-primary p-[20px]">
                <div className="mb-3 inline-flex w-full items-center justify-between">
                  <span className={cn('text-sm  font-semibold text-text-tertiary')}>
                    {t('campaign_settings.versions.enabled')}
                  </span>

                  <Toggle
                    size="sm"
                    checked={!!liveops_advanced_mode_enabled}
                    onChange={e => {
                      setValue('liveops_advanced_mode_enabled', e.target.checked, { shouldDirty: true })
                    }}
                  />
                </div>

                {isSuperAdmin && (
                  <div className="inline-flex w-full items-center justify-between">
                    <span className={cn('flex  items-center gap-1 text-sm font-semibold text-text-tertiary')}>
                      {t('campaign_settings.node-analytics.enabled')}
                      <SuperAdminInfo />
                    </span>

                    <Toggle
                      size="sm"
                      checked={!!node_analytics_enabled}
                      onChange={e => {
                        setValue('node_analytics_enabled', e.target.checked, { shouldDirty: true })
                      }}
                    />
                  </div>
                )}
              </div>
            </FrequencyGroup>
          </div>
        </section>
      </div>
    </FullscreenLayout>
  )
}
